import {PhotoSwipeOptions} from "photoswipe";
import SwiperElement from "../molecules/SwiperElement";
import {SwiperOptions, SwiperElementType, SwiperType} from "../molecules/SwiperTypes";
import {Tracking} from "../../tracking/tracking";
import PinitComponent from "../pinitComponent";

declare const c: {
  revealPopup: (selector: string) => void;
};

declare const photoSwipeItems: [{src: string; title: string; width: number; height: number}] | undefined;

/**
 * Swiper Produktseiten
 */
class ProductSwiper {
  private static openPhotoSwipe(index: number) {
    const pswpElement = document.querySelector<HTMLElement>(".pswp");

    if (photoSwipeItems !== undefined && pswpElement) {
      const options: PhotoSwipeOptions = {
        index,
        dataSource: photoSwipeItems,
      };
      Object.assign(options, photoSwipeOptions);

      void import(/* webpackPrefetch: true */ "photoswipe").then((photoswipe) => {
        const photoswipeDefault = new photoswipe.default(options);
        photoswipeDefault.on("uiRegister", () => {
          // `name` is a ynamically generated class name, with `.pswp__` prefix.
          // See `photoswipe.scss` -> `.pswp__custom-caption` class.
          photoswipeDefault.ui?.registerElement({
            name: "custom-caption",
            isButton: false,
            appendTo: "root",
            onInit: (el, pswp) => {
              pswp.on("change", () => {
                el.innerHTML = pswp.currSlide?.data.title as string;
              });
            },
          });
        });
        photoswipeDefault.init();
      });
    }
  }

  private zoomSwiper: SwiperElementType | undefined;

  constructor(swiperDiv: HTMLDivElement) {
    const swiperElement = swiperDiv.querySelector<SwiperElementType>("swiper-container");
    if (swiperElement) {
      // Has to be set here, and not in the xsl tag attribs, since this would lead to a broken pagination
      swiperElement.setAttribute("loop", "true");

      const slideChange = slideChangeTracker("Product.EmbeddedSwiper.Swiped");
      swiperElement.addEventListener("swiperslidechange", (event: CustomEvent<SwiperType[]>) =>
        slideChange(event.detail[0]),
      );

      this.initZoomHandlers(swiperElement.swiper);
    }
  }

  private initZoomHandlers(swiper: SwiperType) {
    swiper.slides.forEach((slide) => {
      const zoomAnchor = slide.querySelector<HTMLAnchorElement>("a.gallery-zoom");
      if (!zoomAnchor) return;

      zoomAnchor.addEventListener("click", (evt) => {
        evt.preventDefault();
        Tracking.sendEvent("Product.EmbeddedSwiper.Slide.Clicked");

        const initialSlide = swiper.realIndex;
        if (window.matchMedia("(max-width:480px)").matches) {
          ProductSwiper.openPhotoSwipe(initialSlide);

          return;
        }
        c.revealPopup("#GalleryPopup");

        if (typeof this.zoomSwiper === "undefined") {
          const params = Object.assign({}, paramsZoom, {initialSlide});
          const zoomSwiperDiv = document.body.querySelector<HTMLDivElement>("#GalleryPopup");
          if (zoomSwiperDiv) {
            this.zoomSwiper = SwiperElement.run(zoomSwiperDiv, params);
            PinitComponent.pinit(zoomSwiperDiv);
          }
        }

        if (typeof this.zoomSwiper?.swiper.slideToLoop === "function") {
          this.zoomSwiper.swiper.slideToLoop(initialSlide, 0, false);
        }
      });
    });
  }
}

export default ProductSwiper;

// Params for zoom swiper, also used for outlet swiper
export const paramsZoom: SwiperOptions = {
  loop: true,
  navigation: {
    nextEl: ".swiper-button-next-zoom",
    prevEl: ".swiper-button-prev-zoom",
  },
  on: {
    slideChange: slideChangeTracker("Product.FullscreenSwiper.Swiped"),
  },
  pagination: {
    clickable: true,
    el: ".swiper-pagination-zoom",
  },
  simulateTouch: false,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
  spaceBetween: 10,
};

const photoSwipeOptions: PhotoSwipeOptions = {
  bgOpacity: 0.7,
  hideAnimationDuration: 0,
  loop: false,
  showAnimationDuration: 0,
  tapAction: "close",
  arrowNext: false,
  arrowPrev: false,
};

type TrackingFunction = (swiper: SwiperType) => void;

export function slideChangeTracker(eventName: string): TrackingFunction {
  return (swiper: SwiperType) => {
    if (swiper.previousRealIndex !== swiper.realIndex) {
      Tracking.sendEvent(eventName);
    }
  };
}
